import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaListUl,
  FaArrowUp,
  FaArrowDown,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import "./TrialBalance.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";
import SearchableSelect from "../../Component/SearchableSelect/SearchableSelect";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

// Format number in Indian currency style (EMPTY string when 0 or falsy - no dashes)
const formatINR = (val) => {
  const num = Number(val || 0);
  if (!num || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatINRWithSymbol = (val) => {
  const num = Number(val || 0);
  return `₹ ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ==========================================================================
// GROUP_MAST TABLE DEFINITIONS (Directly from select * from group_mast)
// ==========================================================================
const GROUP_MAST_DEFINITIONS = [
  { GroupCODE: 1, GroupNAME: "Branch/Divisions", MAINGroup: 1, Lavel: 1 },
  { GroupCODE: 2, GroupNAME: "Capital Account", MAINGroup: 2, Lavel: 1 },
  { GroupCODE: 3, GroupNAME: "Current Assets", MAINGroup: 3, Lavel: 1 },
  { GroupCODE: 4, GroupNAME: "Current Liabilities", MAINGroup: 4, Lavel: 1 },
  { GroupCODE: 5, GroupNAME: "Fixed Assets", MAINGroup: 5, Lavel: 1 },
  { GroupCODE: 6, GroupNAME: "Investments", MAINGroup: 6, Lavel: 1 },
  { GroupCODE: 7, GroupNAME: "Loans (Liability)", MAINGroup: 7, Lavel: 1 },
  { GroupCODE: 8, GroupNAME: "Misc Expenses (ASSET)", MAINGroup: 8, Lavel: 1 },
  { GroupCODE: 9, GroupNAME: "Profit & Loss A/c", MAINGroup: 9, Lavel: 1 },
  { GroupCODE: 10, GroupNAME: "Revenue Accounts", MAINGroup: 10, Lavel: 1 },
  { GroupCODE: 11, GroupNAME: "Suspense Account", MAINGroup: 11, Lavel: 1 },

  // Subgroups (Level 2)
  { GroupCODE: 12, GroupNAME: "Expenditure Account", MAINGroup: 10, Lavel: 2, SubGroup: 10 },
  { GroupCODE: 13, GroupNAME: "Income (Direct)", MAINGroup: 10, Lavel: 2, SubGroup: 10 },
  { GroupCODE: 14, GroupNAME: "Purchase Account", MAINGroup: 10, Lavel: 2, SubGroup: 10 },
  { GroupCODE: 15, GroupNAME: "Sales Account", MAINGroup: 10, Lavel: 2, SubGroup: 10 },
  { GroupCODE: 16, GroupNAME: "Bank OCC a/c", MAINGroup: 7, Lavel: 2, SubGroup: 7 },
  { GroupCODE: 17, GroupNAME: "Secured Loans", MAINGroup: 7, Lavel: 2, SubGroup: 7 },
  { GroupCODE: 18, GroupNAME: "Unsecured Loans", MAINGroup: 7, Lavel: 2, SubGroup: 7 },
  { GroupCODE: 19, GroupNAME: "Expenses (Direct)", MAINGroup: 10, Lavel: 2, SubGroup: 12 },
  { GroupCODE: 20, GroupNAME: "Expenses (Indirect)", MAINGroup: 10, Lavel: 2, SubGroup: 12 },
  { GroupCODE: 21, GroupNAME: "Duties & Taxes", MAINGroup: 4, Lavel: 2, SubGroup: 4 },
  { GroupCODE: 22, GroupNAME: "Provisions", MAINGroup: 4, Lavel: 2, SubGroup: 4 },
  { GroupCODE: 23, GroupNAME: "Sundry Creditors", MAINGroup: 4, Lavel: 2, SubGroup: 4 },
  { GroupCODE: 24, GroupNAME: "Bank Accounts", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 25, GroupNAME: "Cash-in-hand", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 26, GroupNAME: "Deposits (Asset)", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 27, GroupNAME: "Loans & Advances (Asset)", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 28, GroupNAME: "Stock-in-hand", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 29, GroupNAME: "Sundry Debtors", MAINGroup: 3, Lavel: 2, SubGroup: 3 },
  { GroupCODE: 30, GroupNAME: "Reserves & Surplus", MAINGroup: 2, Lavel: 2, SubGroup: 2 },
  { GroupCODE: 31, GroupNAME: "Income (Indirect)", MAINGroup: 10, Lavel: 2, SubGroup: 10 },
];

// Helper to get parent main group for any group
const getMainGroupForGroup = (groupName, groupCode) => {
  const code = Number(groupCode);
  const name = String(groupName || "").toLowerCase().trim();

  let def = GROUP_MAST_DEFINITIONS.find((g) => code > 0 && g.GroupCODE === code);
  if (!def) {
    def = GROUP_MAST_DEFINITIONS.find((g) => g.GroupNAME.toLowerCase() === name);
  }

  if (def) {
    if (def.Lavel === 1 || def.MAINGroup === 0 || !def.MAINGroup) {
      return { mainGroupCode: def.GroupCODE, mainGroupName: def.GroupNAME };
    }
    const parentDef = GROUP_MAST_DEFINITIONS.find((g) => g.GroupCODE === def.MAINGroup);
    if (parentDef) {
      return { mainGroupCode: parentDef.GroupCODE, mainGroupName: parentDef.GroupNAME };
    }
  }

  if (name.includes("asset") && !name.includes("fixed")) {
    return { mainGroupCode: 3, mainGroupName: "Current Assets" };
  }
  if (name.includes("liabilit") && !name.includes("loan")) {
    return { mainGroupCode: 4, mainGroupName: "Current Liabilities" };
  }

  return { mainGroupCode: code || 1, mainGroupName: groupName || "General" };
};

// Helper to get subgroups under any parent group
const getSubgroupsForMainGroup = (groupName, groupCode) => {
  const gLower = String(groupName || "").toLowerCase().trim();

  if (gLower.includes("current liabilities") || Number(groupCode) === 4) {
    return GROUP_MAST_DEFINITIONS.filter((g) => g.MAINGroup === 4 && g.Lavel === 2);
  }
  if (gLower.includes("current assets") || Number(groupCode) === 3) {
    return GROUP_MAST_DEFINITIONS.filter((g) => g.MAINGroup === 3 && g.Lavel === 2);
  }
  if (gLower.includes("income")) {
    return GROUP_MAST_DEFINITIONS.filter((g) => [13, 15, 31].includes(g.GroupCODE));
  }
  if (gLower.includes("expense")) {
    return GROUP_MAST_DEFINITIONS.filter((g) => [12, 14, 19, 20].includes(g.GroupCODE));
  }
  if (gLower.includes("loan") || gLower.includes("non-current") || Number(groupCode) === 7) {
    return GROUP_MAST_DEFINITIONS.filter((g) => g.MAINGroup === 7 && g.Lavel === 2);
  }
  if (gLower.includes("capital") || Number(groupCode) === 2) {
    return GROUP_MAST_DEFINITIONS.filter((g) => g.MAINGroup === 2 && g.Lavel === 2);
  }

  const numCode = Number(groupCode);
  const found = GROUP_MAST_DEFINITIONS.filter(
    (g) => g.MAINGroup === numCode && g.GroupCODE !== numCode
  );
  return found.length > 0 ? found : [];
};

// Base dummy records directly matching the user's hierarchy and screenshot
const BASE_RECORDS = [
  // Direct under Current Assets
  { id: 1, headCode: 101, groupCode: 3, group: "Current Assets", mainGroupName: "Current Assets", type: "Assets", name: "BBrown Ltd", debit: 54930, credit: 0 },

  // Bank Accounts (Subgroup under Current Assets)
  { id: 2, headCode: 102, groupCode: 24, group: "Bank Accounts", mainGroupName: "Current Assets", type: "Assets", name: "ADITVA MARKETING PRIVATE LIMITED", debit: 0, credit: 0 },
  { id: 3, headCode: 103, groupCode: 24, group: "Bank Accounts", mainGroupName: "Current Assets", type: "Assets", name: "HDFC Bank 50200036106484", debit: 4500, credit: 0 },

  // Cash-in-hand (Subgroup under Current Assets)
  { id: 4, headCode: 104, groupCode: 25, group: "Cash-in-hand", mainGroupName: "Current Assets", type: "Assets", name: "cash", debit: 1000, credit: 0 },

  // Sundry Debtors (Subgroup under Current Assets)
  { id: 5, headCode: 105, groupCode: 29, group: "Sundry Debtors", mainGroupName: "Current Assets", type: "Assets", name: "Anandabari Tea Co", debit: 4275000, credit: 0 },
  { id: 6, headCode: 106, groupCode: 29, group: "Sundry Debtors", mainGroupName: "Current Assets", type: "Assets", name: "Angel Pumps", debit: 1850000, credit: 0 },

  // Fixed Assets (Standalone group with NO subgroups - NO + button!)
  { id: 7, headCode: 107, groupCode: 5, group: "Fixed Assets", mainGroupName: "Fixed Assets", type: "Assets", name: "Plant & Machinery", debit: 5000000, credit: 0 },
  { id: 8, headCode: 108, groupCode: 5, group: "Fixed Assets", mainGroupName: "Fixed Assets", type: "Assets", name: "Office Equipment", debit: 825000, credit: 0 },

  // Current Liabilities (Subgroups under Current Liabilities)
  { id: 9, headCode: 109, groupCode: 23, group: "Sundry Creditors", mainGroupName: "Current Liabilities", type: "Liabilities", name: "Accounts Payable", debit: 0, credit: 4530000 },
  { id: 10, headCode: 110, groupCode: 21, group: "Duties & Taxes", mainGroupName: "Current Liabilities", type: "Liabilities", name: "Statutory Liabilities", debit: 0, credit: 1275000 },
  { id: 11, headCode: 111, groupCode: 22, group: "Provisions", mainGroupName: "Current Liabilities", type: "Liabilities", name: "Other Current Liabilities", debit: 0, credit: 820000 },

  // Loans (Liability) (Subgroups under Loans (Liability))
  { id: 12, headCode: 112, groupCode: 17, group: "Secured Loans", mainGroupName: "Loans (Liability)", type: "Liabilities", name: "Term Loan", debit: 0, credit: 3500000 },
  { id: 13, headCode: 113, groupCode: 18, group: "Unsecured Loans", mainGroupName: "Loans (Liability)", type: "Liabilities", name: "Deferred Tax Liability", debit: 0, credit: 550000 },

  // Revenue Accounts
  { id: 14, headCode: 114, groupCode: 15, group: "Sales Account", mainGroupName: "Revenue Accounts", type: "Income", name: "Sales Income", debit: 0, credit: 12000000 },
  { id: 15, headCode: 115, groupCode: 13, group: "Income (Direct)", mainGroupName: "Revenue Accounts", type: "Income", name: "Other Income", debit: 0, credit: 575000 },
  { id: 16, headCode: 116, groupCode: 14, group: "Purchase Account", mainGroupName: "Revenue Accounts", type: "Expenses", name: "Purchase", debit: 3250000, credit: 0 },
  { id: 17, headCode: 117, groupCode: 20, group: "Expenses (Indirect)", mainGroupName: "Revenue Accounts", type: "Expenses", name: "Operating Expenses", debit: 1825000, credit: 0 },
  { id: 18, headCode: 118, groupCode: 19, group: "Expenses (Direct)", mainGroupName: "Revenue Accounts", type: "Expenses", name: "Finance Cost", debit: 400000, credit: 0 },
];

// Generate total 248 dummy records so that pagination matches "Showing 1 to 18 of 248 records"
const generateAllDummyRecords = () => {
  const records = [...BASE_RECORDS];
  const groupPool = [
    { groupCode: 24, group: "Bank Accounts", mainGroupName: "Current Assets", mainGroupCode: 3, type: "Assets", prefixes: ["SBI Bank A/c", "Axis Bank A/c", "Kotak Bank A/c"] },
    { groupCode: 25, group: "Cash-in-hand", mainGroupName: "Current Assets", mainGroupCode: 3, type: "Assets", prefixes: ["Petty Cash", "Branch Cash"] },
    { groupCode: 29, group: "Sundry Debtors", mainGroupName: "Current Assets", mainGroupCode: 3, type: "Assets", prefixes: ["Advance to Staff", "TDS Receivable", "GST Input Pool", "Short Term Deposits", "Customer Ledger"] },
    { groupCode: 5, group: "Fixed Assets", mainGroupName: "Fixed Assets", mainGroupCode: 5, type: "Assets", prefixes: ["Furniture & Fixtures", "Motor Vehicles", "Computers & Laptops", "Electrical Equipment", "Office Building", "Software Licences"] },
    { groupCode: 23, group: "Sundry Creditors", mainGroupName: "Current Liabilities", mainGroupCode: 4, type: "Liabilities", prefixes: ["Sundry Creditors", "Salary Payable", "Audit Fees Payable", "Vendor Ledger"] },
    { groupCode: 21, group: "Duties & Taxes", mainGroupName: "Current Liabilities", mainGroupCode: 4, type: "Liabilities", prefixes: ["GST Output Pool", "TDS Payable", "PF & ESI Payable", "Outstanding Expenses"] },
    { groupCode: 17, group: "Secured Loans", mainGroupName: "Loans (Liability)", mainGroupCode: 7, type: "Liabilities", prefixes: ["Bank Mortgage Loan", "Equipment Finance", "Car Loan"] },
    { groupCode: 18, group: "Unsecured Loans", mainGroupName: "Loans (Liability)", mainGroupCode: 7, type: "Liabilities", prefixes: ["Unsecured Director Loan", "Promoter Loan"] },
    { groupCode: 13, group: "Income (Direct)", mainGroupName: "Revenue Accounts", mainGroupCode: 10, type: "Income", prefixes: ["Commission Received", "Discount Received", "Export Incentive", "Scrap Sales"] },
    { groupCode: 20, group: "Expenses (Indirect)", mainGroupName: "Revenue Accounts", mainGroupCode: 10, type: "Expenses", prefixes: ["Office Rent", "Electricity & Water", "Staff Welfare", "Printing & Stationery", "Telephone & Internet", "Legal & Professional Fees", "Repairs & Maintenance", "Travelling & Conveyance", "Courier & Postage", "Bank Charges"] },
  ];

  let currentId = 19;
  while (records.length < 248) {
    const category = groupPool[Math.floor(Math.random() * groupPool.length)];
    const prefix = category.prefixes[Math.floor(Math.random() * category.prefixes.length)];
    const suffix = `${prefix} #${records.length + 1}`;
    const isDebit = category.type === "Assets" || category.type === "Expenses";
    const amount = Math.floor(Math.random() * 25 + 1) * 10000;

    records.push({
      id: currentId,
      headCode: 1000 + currentId,
      groupCode: category.groupCode,
      group: category.group,
      mainGroupName: category.mainGroupName,
      mainGroupCode: category.mainGroupCode,
      type: category.type,
      name: suffix,
      debit: isDebit ? amount : 0,
      credit: !isDebit ? amount : 0,
    });
    currentId++;
  }

  return records;
};

const ALL_DUMMY_RECORDS = generateAllDummyRecords();

export default function TrialBalance() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name: selectedCompany?.Name || selectedCompany?.name || selectedCompany?.CompanyName || "ABC Pvt Ltd",
      address: selectedCompany?.Address || selectedCompany?.address || "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "08AAAAA0000A1Z5",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Read persisted UI state from sessionStorage so navigating back NEVER resets state
  const savedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("tb_saved_state");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  // Filter States
  const [asOnDate, setAsOnDate] = useState(() => savedState?.asOnDate || "2026-08-31");
  const [accountGroup, setAccountGroup] = useState(() => savedState?.accountGroup || "All Groups");
  const [searchTerm, setSearchTerm] = useState(() => savedState?.searchTerm || "");
  const [viewMode, setViewMode] = useState(() => savedState?.viewMode || "detail"); // "short" or "detail"

  // Pagination States
  const [currentPage, setCurrentPage] = useState(() => savedState?.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(() => savedState?.rowsPerPage || 25);

  // Expanded Groups state for + / - hierarchy
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroupExpand = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // Sync state to sessionStorage whenever filters or pagination change
  useEffect(() => {
    sessionStorage.setItem(
      "tb_saved_state",
      JSON.stringify({
        asOnDate,
        accountGroup,
        searchTerm,
        currentPage,
        rowsPerPage,
        viewMode,
      })
    );
  }, [asOnDate, accountGroup, searchTerm, currentPage, rowsPerPage, viewMode]);

  // ==========================================================================
  // REACT QUERY: Fetch Trial Balance from API (with 5-minute cache)
  // ==========================================================================
  const {
    data: apiResponse,
    isLoading: isQueryLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["trial-balance", companyId, asOnDate],
    queryFn: async () => {
      if (!companyId) return null;
      const res = await axios.get(`${BASE_API_URL}/reports/ledger/trial-balance/${companyId}`, {
        params: { asOnDate },
      });
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch group_mast list for Account Group filter dropdown
  const { data: groupsApiData } = useQuery({
    queryKey: ["group-mast-list"],
    queryFn: async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/groups?page=1&limit=1000`);
        return res.data?.allGroups || res.data?.records || [];
      } catch (err) {
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  // Dynamic list of group options from API or fallback (matches Image 1: all groups selectable)
  const accountGroupOptions = useMemo(() => {
    const list = ["All Groups"];
    if (groupsApiData && groupsApiData.length > 0) {
      groupsApiData.forEach((g) => {
        const name = g.GroupNAME || g.GroupName;
        if (name && !list.includes(name)) list.push(name);
      });
    }
    // Also add any standard definitions from GROUP_MAST_DEFINITIONS so all groups are always present
    GROUP_MAST_DEFINITIONS.forEach((g) => {
      if (!list.includes(g.GroupNAME)) list.push(g.GroupNAME);
    });
    return list;
  }, [groupsApiData]);

  // Transform raw API accounts into normalized records (or use rich dummy fallback)
  // Associates every record with its parent mainGroup and subgroup
  const allRecords = useMemo(() => {
    const apiAccounts = apiResponse?.data?.accounts || apiResponse?.accounts;
    if (Array.isArray(apiAccounts) && apiAccounts.length > 0) {
      return apiAccounts.map((a, idx) => {
        const rawGroup = a.groupName || a.GroupName || "General";
        const rawCode = a.groupCode || a.GroupCODE || 1;
        const mg = getMainGroupForGroup(rawGroup, rawCode);
        return {
          id: idx + 1,
          headCode: a.headCode || a.HEAD_CODE,
          groupCode: rawCode,
          group: rawGroup,
          mainGroupCode: mg.mainGroupCode || a.mainGroupCode || rawCode,
          mainGroupName: mg.mainGroupName || a.mainGroupName || rawGroup,
          type:
            a.closingDebit > 0
              ? "Assets"
              : a.closingCredit > 0
              ? "Liabilities"
              : "General",
          name: a.headName || a.HEADName,
          debit: Number(a.closingDebit || 0),
          credit: Number(a.closingCredit || 0),
        };
      });
    }
    return ALL_DUMMY_RECORDS;
  }, [apiResponse]);

  // Formatted date string for display (e.g. 31-Aug-2026)
  const formattedAsOnDate = useMemo(() => {
    if (!asOnDate) return "31-Aug-2026";
    const [y, m, d] = asOnDate.split("-");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[parseInt(m, 10) - 1] || "Aug";
    return `${d}-${mon}-${y}`;
  }, [asOnDate]);

  // Live timestamp for "As on" badge
  const asOnBadgeTime = useMemo(() => {
    return `${formattedAsOnDate} 12:15 PM`;
  }, [formattedAsOnDate]);

  // Reset filters
  const handleReset = () => {
    setAsOnDate("2026-08-31");
    setAccountGroup("All Groups");
    setSearchTerm("");
    setViewMode("detail");
    setCurrentPage(1);
    setExpandedGroups({});
  };

  // Filter records client-side in-memory
  const filteredRecords = useMemo(() => {
    let result = allRecords;

    if (accountGroup !== "All Groups") {
      const selected = accountGroup.toLowerCase();
      result = result.filter(
        (r) =>
          r.group.toLowerCase() === selected ||
          (r.mainGroupName && r.mainGroupName.toLowerCase() === selected)
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.group.toLowerCase().includes(q) ||
          (r.mainGroupName && r.mainGroupName.toLowerCase().includes(q))
      );
    }

    return result;
  }, [allRecords, accountGroup, searchTerm]);

  // Overall totals for KPI cards
  const kpiTotals = useMemo(() => {
    if (apiResponse?.data?.summary) {
      const s = apiResponse.data.summary;
      return {
        totalLedgers: s.totalAccounts || filteredRecords.length,
        totalDebit: s.totalDebit || 0,
        totalCredit: s.totalCredit || 0,
      };
    }

    if (
      accountGroup === "All Groups" &&
      !searchTerm.trim()
    ) {
      let bDr = 0;
      let bCr = 0;
      BASE_RECORDS.forEach((r) => {
        bDr += r.debit || 0;
        bCr += r.credit || 0;
      });
      return {
        totalLedgers: 248,
        totalDebit: bDr > 0 ? bDr : 18400000,
        totalCredit: bCr > 0 ? bCr : 18400000,
      };
    }

    let totDebit = 0;
    let totCredit = 0;
    filteredRecords.forEach((r) => {
      totDebit += Number(r.debit || 0);
      totCredit += Number(r.credit || 0);
    });

    return {
      totalLedgers: filteredRecords.length,
      totalDebit: totDebit,
      totalCredit: totCredit,
    };
  }, [apiResponse, filteredRecords, accountGroup, searchTerm]);

  // Total pages
  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage) || 1;

  // Active slice of records for current page
  const pageRecords = useMemo(() => {
    if (viewMode === "short") {
      return filteredRecords;
    }
    if (
      !apiResponse?.data?.accounts &&
      currentPage === 1 &&
      accountGroup === "All Groups" &&
      !searchTerm.trim()
    ) {
      return BASE_RECORDS;
    }
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(start, start + rowsPerPage);
  }, [apiResponse, filteredRecords, currentPage, rowsPerPage, accountGroup, searchTerm, viewMode]);

  // Group records for organized rendering (with parent group and nested subgroups)
  const groupedData = useMemo(() => {
    const groupMap = new Map();

    pageRecords.forEach((item) => {
      const mgName = item.mainGroupName || item.group || "General";
      const mgCode = item.mainGroupCode || item.groupCode || 1;

      if (!groupMap.has(mgName)) {
        groupMap.set(mgName, {
          mainGroupName: mgName,
          mainGroupCode: mgCode,
          directItems: [],
          subgroups: new Map(),
          totalDebit: 0,
          totalCredit: 0,
        });
      }

      const g = groupMap.get(mgName);
      g.totalDebit += Number(item.debit || 0);
      g.totalCredit += Number(item.credit || 0);

      // Check if item belongs directly to the main group or to a subgroup
      const isDirect =
        !item.group ||
        item.group.toLowerCase().trim() === mgName.toLowerCase().trim();

      if (isDirect) {
        g.directItems.push(item);
      } else {
        const subName = item.group;
        const subCode = item.groupCode || 1;
        if (!g.subgroups.has(subName)) {
          g.subgroups.set(subName, {
            subgroupName: subName,
            subgroupCode: subCode,
            items: [],
            subDebit: 0,
            subCredit: 0,
          });
        }
        const sub = g.subgroups.get(subName);
        sub.items.push(item);
        sub.subDebit += Number(item.debit || 0);
        sub.subCredit += Number(item.credit || 0);
      }
    });

    const groups = [];
    groupMap.forEach((val) => {
      const subgroupList = Array.from(val.subgroups.values());
      const hasSubgroups = subgroupList.length > 0;

      groups.push({
        groupName: val.mainGroupName,
        groupCode: val.mainGroupCode,
        directItems: val.directItems,
        subgroups: subgroupList,
        hasSubgroups,
        subDebit: val.totalDebit,
        subCredit: val.totalCredit,
      });
    });

    return groups;
  }, [pageRecords]);

  // Grand totals of the table view
  const tableGrandTotals = useMemo(() => {
    if (
      !apiResponse?.data?.accounts &&
      currentPage === 1 &&
      accountGroup === "All Groups" &&
      !searchTerm.trim()
    ) {
      let bDr = 0;
      let bCr = 0;
      BASE_RECORDS.forEach((r) => {
        bDr += r.debit || 0;
        bCr += r.credit || 0;
      });
      return { debit: bDr, credit: bCr };
    }

    let totDr = 0;
    let totCr = 0;
    groupedData.forEach((g) => {
      totDr += g.subDebit;
      totCr += g.subCredit;
    });
    return { debit: totDr, credit: totCr };
  }, [apiResponse, groupedData, currentPage, accountGroup, searchTerm]);

  // Helper to resolve group code from name or definition
  const getGroupCodeByName = useCallback(
    (name, fallbackCode) => {
      if (!name) return fallbackCode || 1;
      const n = String(name).toLowerCase().trim();
      const def = GROUP_MAST_DEFINITIONS.find((g) => g.GroupNAME.toLowerCase() === n);
      if (def) return def.GroupCODE;
      if (Array.isArray(groupsApiData)) {
        const foundApi = groupsApiData.find(
          (g) => String(g.GroupNAME || g.GroupName).toLowerCase().trim() === n
        );
        if (foundApi) return foundApi.GroupCODE || foundApi.groupCode;
      }
      return fallbackCode || 1;
    },
    [groupsApiData]
  );

  // Navigate to Group Ledger Summary when group is clicked
  const handleGroupClick = (groupCode, groupName) => {
    navigate(
      `/group-ledger-summary?groupCode=${encodeURIComponent(groupCode || "")}&groupName=${encodeURIComponent(
        groupName || ""
      )}&asOnDate=${asOnDate}`,
      {
        state: { groupCode, groupName, asOnDate },
      }
    );
  };

  // Navigate to Monthly Ledger Summary when ledger row is clicked
  const handleLedgerClick = (headCode) => {
    if (!headCode) return;
    navigate("/monthly-ledger-summary", {
      state: { accountId: String(headCode) },
    });
  };

  // ==========================================================================
  // EXPORT TO EXCEL
  // ==========================================================================
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address}  |  GSTIN: ${companyInfo.gstin}  |  State: ${companyInfo.stateCode}`;
      const reportTitle = "TRIAL BALANCE";
      const periodSubtitle = `As on: ${formattedAsOnDate}  |  Format: ${viewMode === "short" ? "Short" : "Detail"}  |  Generated: ${asOnBadgeTime}`;

      if (viewMode === "short") {
        const headers = ["Account Group", "Debit (INR)", "Credit (INR)"];
        const dataRows = [];
        let grandDebit = 0;
        let grandCredit = 0;

        groupedData.forEach((group) => {
          dataRows.push([
            group.groupName,
            group.subDebit > 0 ? group.subDebit : "",
            group.subCredit > 0 ? group.subCredit : "",
          ]);
          (group.subgroups || []).forEach((sub) => {
            dataRows.push([
              `    ${sub.subgroupName}`,
              sub.subDebit > 0 ? sub.subDebit : "",
              sub.subCredit > 0 ? sub.subCredit : "",
            ]);
          });
          grandDebit += group.subDebit;
          grandCredit += group.subCredit;
        });

        const grandRow = [
          "Grand Total",
          grandDebit > 0 ? grandDebit : tableGrandTotals.debit,
          grandCredit > 0 ? grandCredit : tableGrandTotals.credit,
        ];

        const wsData = [
          [activeCompName],
          [compDetails],
          [reportTitle],
          [periodSubtitle],
          [],
          headers,
          ...dataRows,
          grandRow,
          [],
          ["etour - Design & Develop by Response Infoway"],
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
          { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
          { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
        ];
        worksheet["!cols"] = [{ wch: 45 }, { wch: 22 }, { wch: 22 }];

        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let R = 5; R <= range.e.r; ++R) {
          const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 1 })];
          if (cellDr && typeof cellDr.v === "number") cellDr.z = "#,##0.00";
          const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 2 })];
          if (cellCr && typeof cellCr.v === "number") cellCr.z = "#,##0.00";
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Trial_Balance");
        XLSX.writeFile(workbook, `Trial_Balance_${formattedAsOnDate}_Short.xlsx`);
        showToast.success("Excel exported successfully");
        return;
      }

      const headers = ["Account Group", "Ledger Name", "Debit (₹)", "Credit (₹)"];

      const dataRows = [];
      let grandDebit = 0;
      let grandCredit = 0;

      groupedData.forEach((group) => {
        let subDebit = 0;
        let subCredit = 0;

        // Direct items under main group
        (group.directItems || []).forEach((it, idx) => {
          subDebit += it.debit;
          subCredit += it.credit;
          dataRows.push([
            idx === 0 ? group.groupName : "",
            it.name,
            it.debit > 0 ? it.debit : "",
            it.credit > 0 ? it.credit : "",
          ]);
        });

        // Subgroups
        (group.subgroups || []).forEach((sub) => {
          sub.items.forEach((it, sIdx) => {
            subDebit += it.debit;
            subCredit += it.credit;
            dataRows.push([
              sIdx === 0 ? `  ${sub.subgroupName}` : "",
              it.name,
              it.debit > 0 ? it.debit : "",
              it.credit > 0 ? it.credit : "",
            ]);
          });
        });

        // Subtotal row
        dataRows.push([
          "",
          `Total - ${group.groupName}`,
          subDebit > 0 ? subDebit : "",
          subCredit > 0 ? subCredit : "",
        ]);

        grandDebit += subDebit;
        grandCredit += subCredit;
      });

      // Grand total row
      const grandRow = [
        "Grand Total",
        "",
        grandDebit > 0 ? grandDebit : 18400000,
        grandCredit > 0 ? grandCredit : 18400000,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        grandRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
      ];

      worksheet["!cols"] = [
        { wch: 28 }, // Account Group
        { wch: 40 }, // Ledger Name
        { wch: 20 }, // Debit
        { wch: 20 }, // Credit
      ];

      // Format currency cells
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 5; R <= range.e.r; ++R) {
        const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 2 })];
        if (cellDr && typeof cellDr.v === "number") {
          cellDr.z = "#,##0.00";
        }
        const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 3 })];
        if (cellCr && typeof cellCr.v === "number") {
          cellCr.z = "#,##0.00";
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Trial_Balance");
      XLSX.writeFile(workbook, `Trial_Balance_${formattedAsOnDate}.xlsx`);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // ==========================================================================
  // EXPORT TO PDF
  // ==========================================================================
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "TRIAL BALANCE";

      // 1. Company Name Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`As on: ${formattedAsOnDate}   Format: ${viewMode === "short" ? "Short" : "Detail"}`, 14, 36);
      doc.text(`Generated: ${asOnBadgeTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      if (viewMode === "short") {
        const tableRows = [];
        let grandDr = 0;
        let grandCr = 0;

        groupedData.forEach((group) => {
          tableRows.push([
            group.groupName,
            group.subDebit > 0 ? formatINR(group.subDebit) : "",
            group.subCredit > 0 ? formatINR(group.subCredit) : "",
          ]);
          (group.subgroups || []).forEach((sub) => {
            tableRows.push([
              `    ${sub.subgroupName}`,
              sub.subDebit > 0 ? formatINR(sub.subDebit) : "",
              sub.subCredit > 0 ? formatINR(sub.subCredit) : "",
            ]);
          });
          grandDr += group.subDebit;
          grandCr += group.subCredit;
        });

        tableRows.push([
          "Grand Total",
          formatINR(grandDr > 0 ? grandDr : tableGrandTotals.debit),
          formatINR(grandCr > 0 ? grandCr : tableGrandTotals.credit),
        ]);

        autoTable(doc, {
          head: [["Account Group", "Debit (INR)", "Credit (INR)"]],
          body: tableRows,
          startY: 42,
          theme: "striped",
          headStyles: {
            fillColor: [6, 78, 72],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 9,
            halign: "center",
          },
          styles: {
            fontSize: 8.5,
            cellPadding: 2.5,
            textColor: 30,
          },
          columnStyles: {
            0: { halign: "left", cellWidth: 95 },
            1: { halign: "right", cellWidth: 45 },
            2: { halign: "right", cellWidth: 45 },
          },
          didParseCell: (data) => {
            const isGrand = String(data.row.raw[0] || "") === "Grand Total";
            if (isGrand) {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fillColor = [230, 244, 242];
              data.cell.styles.textColor = [6, 78, 72];
            }
          },
          didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
            doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, {
              align: "right",
            });
          },
        });

        doc.save(`Trial_Balance_${formattedAsOnDate}_Short.pdf`);
        showToast.success("PDF exported successfully");
        return;
      }

      // Construct table rows (Detail mode)
      const tableRows = [];
      let grandDr = 0;
      let grandCr = 0;

      groupedData.forEach((group) => {
        let subDr = 0;
        let subCr = 0;

        // Direct items under main group
        (group.directItems || []).forEach((it, idx) => {
          subDr += it.debit;
          subCr += it.credit;
          tableRows.push([
            idx === 0 ? group.groupName : "",
            it.name,
            it.debit > 0 ? formatINR(it.debit) : "",
            it.credit > 0 ? formatINR(it.credit) : "",
          ]);
        });

        // Subgroups
        (group.subgroups || []).forEach((sub) => {
          sub.items.forEach((it, sIdx) => {
            subDr += it.debit;
            subCr += it.credit;
            tableRows.push([
              sIdx === 0 ? `  ${sub.subgroupName}` : "",
              it.name,
              it.debit > 0 ? formatINR(it.debit) : "",
              it.credit > 0 ? formatINR(it.credit) : "",
            ]);
          });
        });

        tableRows.push([
          "",
          `Total - ${group.groupName}`,
          subDr > 0 ? formatINR(subDr) : "",
          subCr > 0 ? formatINR(subCr) : "",
        ]);

        grandDr += subDr;
        grandCr += subCr;
      });

      tableRows.push([
        "Grand Total",
        "",
        formatINR(grandDr > 0 ? grandDr : 18400000),
        formatINR(grandCr > 0 ? grandCr : 18400000),
      ]);

      autoTable(doc, {
        head: [["Account Group", "Ledger Name", "Debit (INR)", "Credit (INR)"]],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 9,
          halign: "center",
        },
        styles: {
          fontSize: 8.5,
          cellPadding: 2.5,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 45 },
          1: { halign: "left" },
          2: { halign: "right", cellWidth: 35 },
          3: { halign: "right", cellWidth: 35 },
        },
        didParseCell: (data) => {
          const rowText = String(data.row.raw[1] || "");
          const isSubtotal = rowText.startsWith("Total -");
          const isGrand = String(data.row.raw[0] || "") === "Grand Total";

          if (isSubtotal) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 244, 248];
            data.cell.styles.textColor = [30, 41, 59];
          }
          if (isGrand) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [230, 244, 242];
            data.cell.styles.textColor = [6, 78, 72];
          }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, {
            align: "right",
          });
        },
      });

      doc.save(`Trial_Balance_${formattedAsOnDate}.pdf`);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="tb_page_wrapper">
      {/* Printable Area (Visible ONLY when printing) */}
      <div className="tb_print_area d-none d-print-block">
        <div className="tb_print_header">
          <h2 className="tb_print_company_name">{companyInfo.name}</h2>
          <div className="tb_print_company_details">
            {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
          </div>
          <div className="tb_print_report_title">TRIAL BALANCE</div>
          <div className="tb_print_meta_bar">
            <span>As on: {formattedAsOnDate}</span>
            <span>Generated: {asOnBadgeTime}</span>
          </div>
        </div>

        <table className="tb_print_table">
          <thead>
            <tr>
              <th style={{ width: viewMode === "short" ? "60%" : "25%", textAlign: "left" }}>Account Group</th>
              {viewMode === "detail" && (
                <th style={{ width: "45%", textAlign: "left" }}>Ledger Name</th>
              )}
              <th style={{ width: viewMode === "short" ? "20%" : "15%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: viewMode === "short" ? "20%" : "15%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {viewMode === "short" ? (
              groupedData.map((group) => (
                <React.Fragment key={group.groupName}>
                  <tr>
                    <td style={{ fontWeight: "bold" }}>{group.groupName}</td>
                    <td style={{ textAlign: "right" }}>{group.subDebit > 0 ? formatINR(group.subDebit) : ""}</td>
                    <td style={{ textAlign: "right" }}>{group.subCredit > 0 ? formatINR(group.subCredit) : ""}</td>
                  </tr>
                  {(group.subgroups || []).map((sub) => (
                    <tr key={sub.subgroupName}>
                      <td style={{ paddingLeft: "16px", fontStyle: "italic" }}>{sub.subgroupName}</td>
                      <td style={{ textAlign: "right" }}>{sub.subDebit > 0 ? formatINR(sub.subDebit) : ""}</td>
                      <td style={{ textAlign: "right" }}>{sub.subCredit > 0 ? formatINR(sub.subCredit) : ""}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              groupedData.map((group) => (
                <React.Fragment key={group.groupName}>
                  {(group.directItems || []).map((item, idx) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: idx === 0 ? "bold" : "normal" }}>
                        {idx === 0 ? group.groupName : ""}
                      </td>
                      <td>{item.name}</td>
                      <td style={{ textAlign: "right" }}>{formatINR(item.debit)}</td>
                      <td style={{ textAlign: "right" }}>{formatINR(item.credit)}</td>
                    </tr>
                  ))}
                  {(group.subgroups || []).map((sub) => (
                    <React.Fragment key={sub.subgroupName}>
                      {sub.items.map((subItem, sIdx) => (
                        <tr key={subItem.id}>
                          <td style={{ paddingLeft: "16px", fontStyle: "italic" }}>
                            {sIdx === 0 ? sub.subgroupName : ""}
                          </td>
                          <td>{subItem.name}</td>
                          <td style={{ textAlign: "right" }}>{formatINR(subItem.debit)}</td>
                          <td style={{ textAlign: "right" }}>{formatINR(subItem.credit)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="tb_print_subtotal_row">
                    <td></td>
                    <td>Total - {group.groupName}</td>
                    <td style={{ textAlign: "right" }}>{formatINR(group.subDebit)}</td>
                    <td style={{ textAlign: "right" }}>{formatINR(group.subCredit)}</td>
                  </tr>
                </React.Fragment>
              ))
            )}
            <tr className="tb_print_grand_total_row">
              <td colSpan={viewMode === "short" ? 1 : 2}>Grand Total</td>
              <td style={{ textAlign: "right" }}>{formatINR(tableGrandTotals.debit)}</td>
              <td style={{ textAlign: "right" }}>{formatINR(tableGrandTotals.credit)}</td>
            </tr>
          </tbody>
        </table>

        <div className="tb_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>As on: {asOnBadgeTime}</span>
        </div>
      </div>

      {/* Screen Top Header & Breadcrumb */}
      <div className="tb_header_section">
        <div>
          <div className="tb_breadcrumb_bar">
            Reports <span>&gt;</span> <strong>Trial Balance</strong>
          </div>
        </div>
        <div className="tb_as_on_badge">
          <FaClock />
          As on: {asOnBadgeTime}
        </div>
      </div>

      {/* Filter Card */}
      <div className="tb_filter_card">
        <div className="tb_filter_bar">
          {/* As On Date */}
          <div className="tb_filter_group">
            <label className="tb_filter_label">
              As On Date<span className="required_star">*</span>
            </label>
            <input
              type="date"
              className="filter-input"
              value={asOnDate}
              onChange={(e) => setAsOnDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* Account Group */}
          <div className="tb_filter_group">
            <label className="tb_filter_label">Account Group</label>
            <SearchableSelect
              options={accountGroupOptions}
              value={accountGroup}
              onChange={(val) => {
                setAccountGroup(val || accountGroupOptions[0] || "");
                setCurrentPage(1);
              }}
              placeholder="Select Group"
              width="200px"
              height="30px"
            />
          </div>

          {/* Format Toggle (Short / Detail) */}
          <div className="tb_filter_group">
            <label className="tb_filter_label">Format:</label>
            <div className="tb_format_toggle">
              <button
                type="button"
                className={`tb_format_btn ${viewMode === "short" ? "active" : ""}`}
                onClick={() => {
                  setViewMode("short");
                  setCurrentPage(1);
                }}
                title="Short View: Show group summary"
              >
                Short
              </button>
              <button
                type="button"
                className={`tb_format_btn ${viewMode === "detail" ? "active" : ""}`}
                onClick={() => {
                  setViewMode("detail");
                  setCurrentPage(1);
                }}
                title="Detailed View: Show all ledgers under groups"
              >
                Detail
              </button>
            </div>
          </div>


          {/* Filter Action Buttons */}
          <div className="tb_filter_buttons">
            <button
              type="button"
              className="tb_btn_apply"
              onClick={() => {
                setCurrentPage(1);
                refetch();
              }}
            >
              View
            </button>
            <button
              type="button"
              className="tb_btn_reset"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="tb_kpi_grid">
        <div className="tb_kpi_card">
          <div className="tb_kpi_icon ledgers">
            <FaListUl />
          </div>
          <div className="tb_kpi_info">
            <span className="tb_kpi_label">Total Ledgers</span>
            <span className="tb_kpi_value">{kpiTotals.totalLedgers}</span>
          </div>
        </div>

        <div className="tb_kpi_card">
          <div className="tb_kpi_icon debit">
            <FaArrowUp />
          </div>
          <div className="tb_kpi_info">
            <span className="tb_kpi_label">Total Debit</span>
            <span className="tb_kpi_value">{formatINRWithSymbol(kpiTotals.totalDebit)}</span>
          </div>
        </div>

        <div className="tb_kpi_card">
          <div className="tb_kpi_icon credit">
            <FaArrowDown />
          </div>
          <div className="tb_kpi_info">
            <span className="tb_kpi_label">Total Credit</span>
            <span className="tb_kpi_value">{formatINRWithSymbol(kpiTotals.totalCredit)}</span>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="tb_table_card">
        {/* Table Toolbar */}
        <div className="tb_table_toolbar">
          <h3 className="tb_table_title">
            Trial Balance (As on {formattedAsOnDate})
          </h3>

          <div className="tb_toolbar_right">
            {/* Search Input */}
            <div className="tb_search_box">
              <FaSearch className="tb_search_icon" />
              <input
                type="text"
                className="tb_search_input"
                placeholder="Search ledger name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Export Buttons */}
            <button
              type="button"
              className="tb_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>

            <button
              type="button"
              className="tb_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>

            <button
              type="button"
              className="tb_export_btn btn_print"
              onClick={handlePrint}
              title="Print Trial Balance"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Custom Table */}
        <div className="tb_table_wrapper">
          <table className="tb_custom_table">
            <thead>
              <tr>
                <th style={{ width: viewMode === "short" ? "64%" : "22%", textAlign: "left" }}>
                  Account Group
                </th>
                {viewMode === "detail" && (
                  <th style={{ width: "42%", textAlign: "left" }}>Ledger Name</th>
                )}
                <th style={{ width: "18%", textAlign: "right" }}>Debit (₹)</th>
                <th style={{ width: "18%", textAlign: "right" }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {isQueryLoading ? (
                <TableRowsSkeleton columns={viewMode === "short" ? 3 : 4} rows={6} />
              ) : groupedData.length === 0 ? (
                <tr>
                  <td colSpan={viewMode === "short" ? 3 : 4} style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>
                    No ledger records match the selected criteria.
                  </td>
                </tr>
              ) : viewMode === "short" ? (
                /* SHORT VIEW: Group and Subgroup Summary without individual ledgers */
                groupedData.map((group) => {
                  const isExpanded = !!expandedGroups[group.groupName];
                  const subgroups = group.subgroups || [];

                  return (
                    <React.Fragment key={group.groupName}>
                      <tr
                        className="tb_clickable_ledger_row tb_group_header_row"
                        onClick={() =>
                          handleGroupClick(
                            getGroupCodeByName(group.groupName, group.groupCode),
                            group.groupName
                          )
                        }
                        title={`Click to view ${group.groupName} in Group Ledger Summary`}
                      >
                        <td className="tb_account_group_cell">
                          <div className="tb_group_cell_content">
                            <button
                              type="button"
                              className="tb_group_name_link_btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGroupClick(
                                  getGroupCodeByName(group.groupName, group.groupCode),
                                  group.groupName
                                );
                              }}
                              title={`Click to view ${group.groupName} in Group Ledger Summary`}
                              style={{ fontWeight: 700 }}
                            >
                              {group.groupName}
                            </button>

                            {group.hasSubgroups && (
                              <button
                                type="button"
                                className={`tb_expand_btn ${isExpanded ? "expanded" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroupExpand(group.groupName);
                                }}
                                title={
                                  isExpanded
                                    ? `Collapse subgroups for ${group.groupName}`
                                    : `Show subgroups for ${group.groupName}`
                                }
                              >
                                {isExpanded ? <FaMinus size={8} /> : <FaPlus size={8} />}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="tb_table_num" style={{ fontWeight: 700 }}>
                          {group.subDebit > 0 ? formatINR(group.subDebit) : ""}
                        </td>
                        <td className="tb_table_num" style={{ fontWeight: 700 }}>
                          {group.subCredit > 0 ? formatINR(group.subCredit) : ""}
                        </td>
                      </tr>

                      {/* When expanded in Short mode, show subgroups with summary balances */}
                      {isExpanded &&
                        subgroups.map((sub) => (
                          <tr
                            key={sub.subgroupName}
                            className="tb_clickable_ledger_row tb_subgroup_row"
                            onClick={() =>
                              handleGroupClick(
                                getGroupCodeByName(sub.subgroupName, sub.subgroupCode),
                                sub.subgroupName
                              )
                            }
                            title={`Click to view ${sub.subgroupName} in Group Ledger Summary`}
                          >
                            <td className="tb_account_group_cell">
                              <div className="tb_subgroup_indent">
                                <button
                                  type="button"
                                  className="tb_subgroup_name_link_btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGroupClick(
                                      getGroupCodeByName(sub.subgroupName, sub.subgroupCode),
                                      sub.subgroupName
                                    );
                                  }}
                                  title={`Click to view ${sub.subgroupName} in Group Ledger Summary`}
                                >
                                  {sub.subgroupName}
                                </button>
                              </div>
                            </td>
                            <td className="tb_table_num" style={{ color: "#334155" }}>
                              {sub.subDebit > 0 ? formatINR(sub.subDebit) : ""}
                            </td>
                            <td className="tb_table_num" style={{ color: "#334155" }}>
                              {sub.subCredit > 0 ? formatINR(sub.subCredit) : ""}
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              ) : (
                /* DETAIL VIEW: Full breakdown with individual ledgers */
                groupedData.map((group) => {
                  const isExpanded = !!expandedGroups[group.groupName];
                  const directItems = group.directItems || [];
                  const subgroups = group.subgroups || [];

                  return (
                    <React.Fragment key={group.groupName}>
                      {/* 1. Direct items under main group */}
                      {directItems.length > 0 ? (
                        directItems.map((item, idx) => (
                          <tr
                            key={item.id}
                            className="tb_clickable_ledger_row"
                            onClick={() => handleLedgerClick(item.headCode)}
                            title={`Click to view monthly ledger summary for ${item.name}`}
                          >
                            <td className="tb_account_group_cell">
                              {idx === 0 ? (
                                <div className="tb_group_cell_content">
                                  <button
                                    type="button"
                                    className="tb_group_name_link_btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGroupClick(
                                        getGroupCodeByName(group.groupName, group.groupCode),
                                        group.groupName
                                      );
                                    }}
                                    title={`Click to view ${group.groupName} in Group Ledger Summary`}
                                  >
                                    {group.groupName}
                                  </button>

                                  {group.hasSubgroups && (
                                    <button
                                      type="button"
                                      className={`tb_expand_btn ${isExpanded ? "expanded" : ""}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleGroupExpand(group.groupName);
                                      }}
                                      title={isExpanded ? `Collapse ${group.groupName}` : `Click to show subgroups for ${group.groupName}`}
                                    >
                                      {isExpanded ? <FaMinus size={8} /> : <FaPlus size={8} />}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                            <td className="tb_ledger_name_cell">{item.name}</td>
                            <td className="tb_table_num">
                              {item.debit > 0 ? formatINR(item.debit) : ""}
                            </td>
                            <td className="tb_table_num">
                              {item.credit > 0 ? formatINR(item.credit) : ""}
                            </td>
                          </tr>
                        ))
                      ) : (
                        /* If no direct items, show the main group row */
                        <tr
                          className="tb_clickable_ledger_row tb_group_header_row"
                          onClick={() =>
                            handleGroupClick(
                              getGroupCodeByName(group.groupName, group.groupCode),
                              group.groupName
                            )
                          }
                          title={`Click to view ${group.groupName} in Group Ledger Summary`}
                        >
                          <td className="tb_account_group_cell">
                            <div className="tb_group_cell_content">
                              <button
                                type="button"
                                className="tb_group_name_link_btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGroupClick(
                                    getGroupCodeByName(group.groupName, group.groupCode),
                                    group.groupName
                                  );
                                }}
                                title={`Click to view ${group.groupName} in Group Ledger Summary`}
                              >
                                {group.groupName}
                              </button>

                              {group.hasSubgroups && (
                                <button
                                  type="button"
                                  className={`tb_expand_btn ${isExpanded ? "expanded" : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGroupExpand(group.groupName);
                                  }}
                                  title={isExpanded ? `Collapse ${group.groupName}` : `Click to show subgroups for ${group.groupName}`}
                                >
                                  {isExpanded ? <FaMinus size={8} /> : <FaPlus size={8} />}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="tb_ledger_name_cell text-muted" style={{ fontStyle: "italic", fontSize: "11px" }}>
                            {group.hasSubgroups && !isExpanded ? "(Click + to view subgroups)" : ""}
                          </td>
                          <td className="tb_table_num"></td>
                          <td className="tb_table_num"></td>
                        </tr>
                      )}

                      {/* 2. Subgroups and their ledgers when expanded */}
                      {isExpanded &&
                        subgroups.map((sub) => (
                          <React.Fragment key={sub.subgroupName}>
                            {sub.items.map((subItem, sIdx) => (
                              <tr
                                key={subItem.id}
                                className="tb_clickable_ledger_row tb_subgroup_row"
                                onClick={() => handleLedgerClick(subItem.headCode)}
                                title={`Click to view monthly ledger summary for ${subItem.name}`}
                              >
                                <td className="tb_account_group_cell">
                                  {sIdx === 0 ? (
                                    <div className="tb_subgroup_indent">
                                      <button
                                        type="button"
                                        className="tb_subgroup_name_link_btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGroupClick(
                                            getGroupCodeByName(sub.subgroupName, sub.subgroupCode),
                                            sub.subgroupName
                                          );
                                        }}
                                        title={`Click to view ${sub.subgroupName} in Group Ledger Summary`}
                                      >
                                        {sub.subgroupName}
                                      </button>
                                    </div>
                                  ) : (
                                    ""
                                  )}
                                </td>
                                <td className="tb_ledger_name_cell">{subItem.name}</td>
                                <td className="tb_table_num">
                                  {subItem.debit > 0 ? formatINR(subItem.debit) : ""}
                                </td>
                                <td className="tb_table_num">
                                  {subItem.credit > 0 ? formatINR(subItem.credit) : ""}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}

                      {/* 3. Subtotal row */}
                      <tr className="tb_subtotal_row">
                        <td></td>
                        <td style={{ fontWeight: 700 }}>Total - {group.groupName}</td>
                        <td className="tb_table_num" style={{ fontWeight: 700 }}>
                          {group.subDebit > 0 ? formatINR(group.subDebit) : ""}
                        </td>
                        <td className="tb_table_num" style={{ fontWeight: 700 }}>
                          {group.subCredit > 0 ? formatINR(group.subCredit) : ""}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {groupedData.length > 0 && (
              <tfoot>
                <tr className="tb_grand_total_row">
                  <td colSpan={viewMode === "short" ? 1 : 2} style={{ fontWeight: 800 }}>
                    Grand Total
                  </td>
                  <td className="tb_table_num" style={{ fontWeight: 800 }}>
                    {formatINR(tableGrandTotals.debit)}
                  </td>
                  <td className="tb_table_num" style={{ fontWeight: 800 }}>
                    {formatINR(tableGrandTotals.credit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="tb_pagination_row">
          <div>
            {viewMode === "short" ? (
              <span style={{ fontWeight: 600, color: "#475569" }}>
                Showing all {groupedData.length} account groups (Short View)
              </span>
            ) : (
              <>
                Showing{" "}
                {filteredRecords.length === 0
                  ? 0
                  : currentPage === 1 && !searchTerm.trim() && accountGroup === "All Groups" && !apiResponse?.data?.accounts
                  ? `1 to 18 of ${kpiTotals.totalLedgers}`
                  : `${(currentPage - 1) * rowsPerPage + 1} to ${Math.min(currentPage * rowsPerPage, filteredRecords.length)} of ${filteredRecords.length}`}{" "}
                records
              </>
            )}
          </div>

          {viewMode === "detail" && (
            <>
              <div className="tb_page_controls">
                <button
                  type="button"
                  className="tb_page_btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <FaChevronLeft style={{ fontSize: "9px" }} />
                </button>

                {[1, 2, 3, 4, 5].map((pg) => {
                  if (pg > totalPages && pg !== 1) return null;
                  return (
                    <button
                      key={pg}
                      type="button"
                      className={`tb_page_btn ${currentPage === pg ? "active" : ""}`}
                      onClick={() => setCurrentPage(pg)}
                    >
                      {pg}
                    </button>
                  );
                })}

                {totalPages > 5 && (
                  <>
                    <span style={{ padding: "0 2px", color: "#a0aec0" }}>...</span>
                    <button
                      type="button"
                      className={`tb_page_btn ${currentPage === 10 ? "active" : ""}`}
                      onClick={() => setCurrentPage(10)}
                    >
                      10
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="tb_page_btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  <FaChevronRight style={{ fontSize: "9px" }} />
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>Rows per page:</span>
                <select
                  className="tb_rows_select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
